import { createContext, useContext, useMemo } from "react";
import {
  clientViewPartnerPortalBase,
  clientViewPartnerPortalPath,
} from "../../constants/clientViewPartnerPortalRoutes";
import { automapAdminPortalPath } from "../../constants/automapAdminPortalPaths";
import { AUTOMAP_CLIENT_VIEW_SLUG } from "../../constants/automapPartnerPortalConfig";
import { getPartnerPortalConfig } from "../../constants/partnerPortalConfig";

const PartnerPortalBaseContext = createContext(null);

function resolvePortalPaths(clientViewSlug, portalBase) {
  if (portalBase) {
    const normalizedBase = String(portalBase).replace(/\/+$/, "");
    return {
      basePath: normalizedBase,
      loginPath: `${normalizedBase}/login`,
      dashboardPath: `${normalizedBase}/dashboard`,
      path: (segment) => {
        const normalized = String(segment || "").replace(/^\/+/, "");
        return normalized ? `${normalizedBase}/${normalized}` : normalizedBase;
      },
    };
  }

  if (clientViewSlug === AUTOMAP_CLIENT_VIEW_SLUG) {
    return {
      basePath: automapAdminPortalPath(),
      loginPath: automapAdminPortalPath("login"),
      dashboardPath: automapAdminPortalPath("dashboard"),
      path: automapAdminPortalPath,
    };
  }

  return {
    basePath: clientViewPartnerPortalBase(clientViewSlug),
    loginPath: clientViewPartnerPortalPath(clientViewSlug, "login"),
    dashboardPath: clientViewPartnerPortalPath(clientViewSlug, "dashboard"),
    path: (segment) => clientViewPartnerPortalPath(clientViewSlug, segment),
  };
}

export function PartnerPortalBaseProvider({ clientViewSlug, portalBase = null, children }) {
  const value = useMemo(() => {
    const config = getPartnerPortalConfig(clientViewSlug);
    const paths = resolvePortalPaths(clientViewSlug, portalBase);

    return {
      clientViewSlug,
      ...paths,
      portalLabel: config.portalLabel,
      portalSubtitle: config.portalSubtitle,
      portalLoadingLabel:
        config.portalLoadingLabel || `Loading ${config.portalLabel.toLowerCase()}…`,
      portalNavAriaLabel: config.portalNavAriaLabel || "Portal sections",
      tabs: config.tabs,
    };
  }, [clientViewSlug, portalBase]);

  return (
    <PartnerPortalBaseContext.Provider value={value}>{children}</PartnerPortalBaseContext.Provider>
  );
}

export function usePartnerPortalBase() {
  const context = useContext(PartnerPortalBaseContext);
  if (!context) {
    throw new Error("usePartnerPortalBase must be used within PartnerPortalBaseProvider");
  }
  return context;
}
